import React from "react";
import {
  animated,
  useChain,
  useSpringRef,
  useTransition,
} from "@react-spring/web";
import { get } from "lodash";
import cx from "classnames";

import { BannerView } from "src/components/banner";
import { Menubar } from "../components/menuBar";
import DescriptionEditor from "../components/description";
import { BannerEditor } from "../components/banner/library";
import { BannerFilters } from "../components/banner/filter";
import { CropEditor } from "../components/crop";
import Experience from "../components/experience";
import BannerExperienceView from "src/components/bannerExperienceView";

import CollectionWidgetManager from "src/utils/collections.widget.service";

import {
  MenuState,
  SettingsBanner,
} from "src/types/settings/accountManagement";
import { Account } from "src/types/accounts";
import { Banner } from "src/types/common";

import styles from "./styles.module.scss";

interface CollectionSettingsProps {
  data: Partial<Account>;
  /*eslint-disable-next-line no-unused-vars*/
  handleAccountSettingsUpdate: (field: string, value: any) => void;
  resetEditor?: boolean;
}

const CollectionSettings: React.FC<CollectionSettingsProps> = ({
  data,
  handleAccountSettingsUpdate,
  resetEditor,
}) => {
  const [menuState, setMenuState] = React.useState<MenuState>({
    primaryAction: "view",
  });

  const showBanner =
    menuState.primaryAction === "edit-banner" && !menuState.secondaryAction;
  const cropBanner =
    menuState.primaryAction === "edit-banner" &&
    menuState.secondaryAction === "crop";
  const showFilters =
    menuState.primaryAction === "edit-banner" &&
    menuState.secondaryAction === "filter";
  const showPosition =
    menuState.primaryAction === "edit-banner" &&
    menuState.secondaryAction === "position";

  const bannerActive = showBanner || showFilters || showPosition || cropBanner;

  const setDefaultContactCard = get(
    data,
    "collectionSettings.setDefaultContactCard",
    false,
  );

  const bannerEditorRef = useSpringRef();
  const bannerEditorTransitions = useTransition(showBanner, {
    ref: bannerEditorRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: "translate3d(0, 30vh, 0)" },
    enter: { opacity: 1, transform: "translate3d(0, 0px, 0)" },
    config: {
      friction: 14,
      tension: 120,
    },
  });

  const bannerFilterRef = useSpringRef();
  const bannerFilterTransitions = useTransition(showFilters, {
    ref: bannerFilterRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: "translate3d(0, 30vh, 0)" },
    enter: { opacity: 1, transform: "translate3d(0, 0px, 0)" },
    leave: { opacity: 0, transform: "translate3d(0, 30vh, 0)" },
    config: {
      friction: 14,
      tension: 120,
    },
  });

  const cropperRef = useSpringRef();
  const cropperTransitions = useTransition(cropBanner, {
    ref: cropperRef,
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    delay: showBanner ? 0 : 0,
    config: {
      friction: 14,
      tension: 120,
    },
  });

  const showShortBanner =
    menuState.primaryAction !== "view" || !setDefaultContactCard;

  const transitions = useTransition(showShortBanner, {
    key: showShortBanner,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: {
      duration: 300,
      tension: 200,
      friction: 20,
    },
    exitBeforeEnter: true,
  });

  const handleTemplateChange = React.useCallback(
    (templateValue: any) => {
      const settings = data.collectionSettings || {};
      const updated = {
        ...settings,
        template: templateValue,
      };
      handleAccountSettingsUpdate("collectionSettings", updated);
    },
    [data, handleAccountSettingsUpdate],
  );

  const handleFilterChange = (field, newValue) => {
    const updatedCollectionSettings = {
      ...data.collectionSettings,
      [field]: newValue,
    };
    handleAccountSettingsUpdate(
      "collectionSettings",
      updatedCollectionSettings,
    );
  };

  const handleCropChange = (field: string, newValue: any) => {
    if (field === "banner") {
      const updatedCollectionSettings = {
        ...data.collectionSettings,
        banner: newValue,
      };

      handleAccountSettingsUpdate(
        "collectionSettings",
        updatedCollectionSettings,
      );
    }
  };

  const handleBannerChange = React.useCallback(
    (field: string, updatedBanner: Partial<Banner>) => {
      if (field === "banner") {
        const currentSettings: Partial<Account["collectionSettings"]> =
          get(data, "collectionSettings") || {};
        const updatedCollectionSettings = {
          ...currentSettings,
          banner: {
            ...(currentSettings.banner || {}),
            ...updatedBanner,
          },
        };
        handleAccountSettingsUpdate(
          "collectionSettings",
          updatedCollectionSettings,
        );
      }
    },
    [data, handleAccountSettingsUpdate],
  );

  useChain([bannerEditorRef, bannerFilterRef, cropperRef], [0, 0, 0, 0, 0]);

  return (
    <div className={styles.container}>
      <div
        className={cx(
          styles.experienceSection,
          bannerActive ? styles.hide : styles.show,
        )}
      >
        <Experience
          selectedViewerType={data.collectionSettings?.viewerType}
          onChange={(newType) =>
            handleAccountSettingsUpdate("collectionSettings", {
              ...data.collectionSettings,
              viewerType: newType,
            })
          }
        />
      </div>

      <div className={styles.header}>
        <div className={styles.heading}>Default Banner</div>
        <div className={styles.description}>
          Set the visual identity for your collections.
        </div>
      </div>

      <div
        className={cx(styles.viewerSection, {
          [styles.bannerActiveViewer]: bannerActive,
        })}
      >
        <div
          className={cx(styles.bannerWrapper, {
            [styles.paddedBackground]: showShortBanner,
            [styles.showFullExperience]: !showShortBanner,
          })}
        >
          {transitions((style, item) => (
            <animated.div
              style={style}
              className={cx(styles.banner, {
                [styles.hide]: bannerActive,
                [styles.cropping]: cropBanner,
                [styles.show]: !bannerActive,
              })}
            >
              {item ? (
                <>
                  <BannerView
                    banner={
                      get(data, "collectionSettings.banner") as
                      | SettingsBanner
                      | undefined
                    }
                    defaultBanner="/images/collections/background.jpeg"
                  />
                  {menuState.primaryAction === "view" && (
                    <div
                      className={cx(styles.editorOverlay, {
                        [styles.showEditor]: menuState.primaryAction === "view",
                      })}
                    >
                      <div className={styles.inner}>
                        <DescriptionEditor
                          key={resetEditor ? "reset" : "stable"}
                          editorContent={get(
                            data,
                            "collectionSettings.template",
                          )}
                          widgetManager={new CollectionWidgetManager()}
                          maxHeight="none"
                          menuState={{ primaryAction: "view" }}
                          onChange={handleTemplateChange}
                          onMenuChange={(newState) => setMenuState(newState)}
                        />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <BannerExperienceView
                  banner={
                    get(data, "collectionSettings.banner") as
                    | SettingsBanner
                    | undefined
                  }
                  viewerType={get(data, "collectionSettings.viewerType") ?? "MASONRY"}
                  editorContent={get(
                    data,
                    "collectionSettings.template",
                  ) ?? ""}
                  onChange={handleTemplateChange}
                  onMenuChange={(newState) => setMenuState(newState)}
                />
              )}
            </animated.div>
          ))}
          <div
            className={cx(styles.menuBarWrapperTop, {
              [styles.menuBarShifted]: bannerActive,
            })}
          >
            <Menubar
              entitySettings={data.collectionSettings}
              menuState={menuState}
              onChange={setMenuState}
              onChanges={handleAccountSettingsUpdate}
              enableLiveToggle={true}
              entityField="collectionSettings"
            />
          </div>
        </div>
        <div
          className={cx(styles.body, {
            [styles.cropping]: cropBanner,
          })}
        >
          {bannerEditorTransitions(
            (style, item) =>
              item && (
                <animated.div
                  style={style}
                  className={cx(styles.content, styles.bannerEditor)}
                >
                  <BannerEditor
                    collection={get(data, "collectionSettings")}
                    onChange={handleBannerChange}
                  />
                </animated.div>
              ),
          )}

          {bannerFilterTransitions(
            (style, item) =>
              item && (
                <animated.div
                  style={style}
                  className={cx(styles.content, styles.bannerEditor)}
                >
                  {data?.collectionSettings && (
                    <BannerFilters
                      collection={data.collectionSettings}
                      onChange={handleFilterChange}
                    />
                  )}
                </animated.div>
              ),
          )}

          {cropperTransitions(
            (style, show) =>
              show && (
                <animated.div style={style} className={styles.editor}>
                  <CropEditor
                    enabled
                    entity={data.collectionSettings}
                    menuState={menuState}
                    onMenuChange={setMenuState}
                    onChange={handleCropChange}
                  />
                </animated.div>
              ),
          )}
        </div>
      </div>
    </div>
  );
};

export default CollectionSettings;
